import React from "react";
import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { CopyButton } from "@/components/common/CopyButton";
import { formatDateTime } from "@/utils/dateUtils";
import { MessageDelivery } from "@/types/message-delivery";
import { cn } from "@/lib/utils";

interface DeliveryDetailPopoverProps {
  row: MessageDelivery;
}

interface DetailRow {
  key: string;
  value: string;
  mono?: boolean;
  copy?: boolean;
  tone?: "default" | "destructive";
}

const buildRows = (
  row: MessageDelivery,
  t: (key: string) => string,
): DetailRow[] => {
  const rows: DetailRow[] = [];
  const labelKey = (suffix: string) =>
    t(`communicationChannels.deliveries.detail.${suffix}`);

  if (row.providerMessageId) {
    rows.push({
      key: labelKey("providerMessageId"),
      value: row.providerMessageId,
      mono: true,
      copy: true,
    });
  }
  if (row.retryCount > 0) {
    rows.push({ key: labelKey("retries"), value: String(row.retryCount) });
  }
  if (row.errorCode) {
    rows.push({
      key: labelKey("errorCode"),
      value: row.errorCode,
      mono: true,
      tone: "destructive",
    });
  }
  if (row.errorMessage) {
    rows.push({
      key: labelKey("errorMessage"),
      value: row.errorMessage,
      tone: "destructive",
    });
  }

  const timestamps: [string, string | null][] = [
    ["queuedAt", row.queuedAt],
    ["sentAt", row.sentAt],
    ["deliveredAt", row.deliveredAt],
    ["readAt", row.readAt],
    ["failedAt", row.failedAt],
  ];
  timestamps.forEach(([suffix, value]) => {
    if (value) {
      rows.push({ key: labelKey(suffix), value: formatDateTime(value) });
    }
  });

  if (row.streamChannelId) {
    rows.push({
      key: labelKey("streamChannelId"),
      value: row.streamChannelId,
      mono: true,
      copy: true,
    });
  }
  if (row.streamMessageId) {
    rows.push({
      key: labelKey("streamMessageId"),
      value: row.streamMessageId,
      mono: true,
      copy: true,
    });
  }

  return rows;
};

export const DeliveryDetailPopover: React.FC<DeliveryDetailPopoverProps> = ({
  row,
}) => {
  const { t } = useTranslation();
  const rows = buildRows(row, t);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="new_ghost"
          size="icon_sm"
          aria-label={t("communicationChannels.deliveries.detail.openAria")}
        >
          <Info className="size-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="border-base-border flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-3 rounded-xl border bg-white p-4 shadow-md"
      >
        <h4 className="text-sm font-semibold text-black/96">
          {t("communicationChannels.deliveries.columns.details")}
        </h4>
        {rows.length === 0 ? (
          <p className="text-sm text-black/45">
            {t("communicationChannels.deliveries.detail.empty")}
          </p>
        ) : (
          <dl className="flex flex-col gap-2.5">
            {rows.map((detail) => (
              <div key={detail.key} className="flex flex-col gap-0.5">
                <dt className="text-xs font-medium text-black/45">
                  {detail.key}
                </dt>
                <dd className="flex items-start gap-1">
                  <span className={cellClass(detail)} title={detail.value}>
                    {detail.value}
                  </span>
                  {detail.copy && (
                    <CopyButton value={detail.value} className="shrink-0" />
                  )}
                </dd>
              </div>
            ))}
          </dl>
        )}
      </PopoverContent>
    </Popover>
  );
};

const cellClass = (detail: DetailRow): string => {
  const base = "break-all text-sm";
  const tone =
    detail.tone === "destructive"
      ? "text-custom-destructive-dark"
      : "text-black/96";
  return cn(base, tone, detail.mono && "font-mono text-xs");
};
